import * as React from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { NotConfiguredBanner } from "@/components/common/NotConfiguredBanner";
import { ShieldCheck, History, AlertCircle, Database, RefreshCw, Trash2, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/supabase/AuthContext";
import { createBrowserSupabase } from "@/supabase/client";

type Batch = {
  id: string;
  original_filename: string | null;
  status: string;
  source_system: string | null;
  import_type: string | null;
  total_rows: number | null;
  valid_rows: number | null;
  error_rows: number | null;
  created_at: string;
  error_summary?: unknown;
};

function formatBatchStatus(status: string): string {
  const labels: Record<string, string> = {
    parsing: "Okunuyor", parsed: "Okundu", validating: "Doğrulanıyor",
    validated: "Doğrulandı", previewed: "Önizleme hazır", approved: "Onaylandı",
    committing: "Kaydediliyor", committed: "Staging'e kaydedildi",
    failed: "Başarısız", superseded: "Yerine yenisi geçti",
  };
  return labels[status] ?? status;
}

function isTestFixtureBatch(batch: Pick<Batch, "original_filename" | "error_summary">): boolean {
  if ((batch.original_filename ?? "").toLocaleLowerCase("tr-TR").includes("afyon")) return true;
  const summary = batch.error_summary;
  if (summary && typeof summary === "object" && "warehouses" in summary) {
    const warehouses = (summary as { warehouses?: unknown }).warehouses;
    return Array.isArray(warehouses) && warehouses.some((name) =>
      typeof name === "string" && name.toLocaleLowerCase("tr-TR").includes("afyon")
    );
  }
  return false;
}

function displayBatchName(batch: Pick<Batch, "original_filename" | "error_summary">): string {
  return isTestFixtureBatch(batch) ? "Test aktarımı" : (batch.original_filename ?? "Dosya adı olmayan aktarım");
}

const DataControl: React.FC = () => {
  const { companyId, memberships } = useAuth();
  const role = memberships?.find((membership) => membership.company_id === companyId)?.role;
  const canDelete = role === "admin" || role === "partner";
  const factory = React.useMemo(() => createBrowserSupabase(), []);
  const [batches, setBatches] = React.useState<Batch[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<Batch | null>(null);

  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = React.useState(false);
  const [bulkDeleting, setBulkDeleting] = React.useState(false);
  const [bulkDeleteError, setBulkDeleteError] = React.useState<string | null>(null);
  const loadRequestId = React.useRef(0);

  const loadBatches = React.useCallback(async () => {
    const requestId = ++loadRequestId.current;
    if (!factory.ok || !companyId) {
      setBatches([]); setError(null); setLoading(false);
      setPendingDelete(null); setConfirmBulkDelete(false);
      return;
    }
    setLoading(true); setError(null);
    try {
      const { data, error: queryError } = await factory.client.from("import_batches")
        .select("id, original_filename, source_system, import_type, status, total_rows, valid_rows, error_rows, created_at, error_summary")
        .eq("company_id", companyId).order("created_at", { ascending: false }).limit(100);
      if (requestId !== loadRequestId.current) return;
      if (queryError) throw queryError;
      setBatches((data ?? []) as Batch[]);
    } catch (e) {
      if (requestId !== loadRequestId.current) return;
      const err = e as { message?: unknown };
      setBatches([]);
      setError(typeof err?.message === "string" ? err.message : "Aktarım geçmişi yüklenemedi.");
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  }, [factory, companyId]);

  React.useEffect(() => {
    void loadBatches();
    return () => { loadRequestId.current += 1; };
  }, [loadBatches]);

  const cancelDelete = () => {
    setPendingDelete(null);
    setDeleteError(null);
  };

  const fixtureBatches = batches.filter((batch) => isTestFixtureBatch(batch));

  const deleteFixtureBatches = async () => {
    if (!factory.ok || !companyId || !confirmBulkDelete || fixtureBatches.length === 0) return;
    setBulkDeleting(true);
    setBulkDeleteError(null);
    let deleted = 0;
    const failures: string[] = [];
    try {
      for (const batch of fixtureBatches) {
        const { data, error: rpcError } = await factory.client.rpc("delete_import_batch", {
          p_company_id: companyId,
          p_batch_id: batch.id,
        });
        if (rpcError) {
          failures.push(`${displayBatchName(batch)}: ${rpcError.message}`);
          continue;
        }
        if (data && typeof data === "object" && (data as { deleted?: unknown }).deleted === true) {
          deleted += 1;
        } else {
          failures.push(`${displayBatchName(batch)}: silme doğrulanamadı`);
        }
      }
      setConfirmBulkDelete(false);
      await loadBatches();
      if (failures.length > 0) {
        setBulkDeleteError(`${deleted} kayıt silindi. ${failures.length} kayıt silinemedi: ${failures.slice(0, 3).join(" · ")}`);
      } else {
        setBulkDeleteError(null);
      }
    } catch (e) {
      const err = e as { message?: unknown };
      setBulkDeleteError(typeof err?.message === "string" ? err.message : "Kayıtlar silinirken beklenmeyen hata oluştu.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const deleteBatch = async () => {
    if (!factory.ok || !companyId || !pendingDelete) return;
    setDeletingId(pendingDelete.id);
    setDeleteError(null);
    try {
      const { data, error: rpcError } = await factory.client.rpc("delete_import_batch", {
        p_company_id: companyId,
        p_batch_id: pendingDelete.id,
      });
      if (rpcError) throw rpcError;
      if (!data || typeof data !== "object" || (data as { deleted?: unknown }).deleted !== true) {
        throw new Error("Veritabanı silme işlemini doğrulayamadı; kayıt silinmiş kabul edilmedi.");
      }
      cancelDelete();
      await loadBatches();
    } catch (e) {
      const err = e as { message?: unknown };
      setDeleteError(typeof err?.message === "string"
        ? err.message
        : "Aktarım kaydı silinemedi. Veritabanı yetkilerini ve migration durumunu kontrol edin.");
    } finally {
      setDeletingId(null);
    }
  };

  // Keep fixture rows in the audit/history table, but exclude them from operational KPIs.
  const operationalBatches = batches.filter((batch) => !isTestFixtureBatch(batch));
  const failed = operationalBatches.filter((batch) => batch.status === "failed").length;
  const completed = operationalBatches.filter((batch) => batch.status === "committed").length;

  return (
    <div data-testid="page-data-control" className="space-y-8">
      <PageHeader
        eyebrow="Veri İşleme"
        title="Veri Kontrol Merkezi"
        description="İçe aktarma kayıtlarını, başarısız denemeleri ve veri işleme durumlarını buradan takip edin."
      />

      {!factory.ok ? <NotConfiguredBanner /> : null}

      <section aria-label="İçe aktarma özeti" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-5">
          <Database className="h-5 w-5 text-muted-foreground" />
          <div className="mt-3 text-sm text-muted-foreground">Listelenen aktarım</div>
          <div className="num mt-1 text-2xl font-medium text-foreground">{batches.length}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <ShieldCheck className="h-5 w-5 text-muted-foreground" />
          <div className="mt-3 text-sm text-muted-foreground">Staging'e kaydedilen</div>
          <div className="num mt-1 text-2xl font-medium text-foreground">{completed}</div>
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <AlertCircle className="h-5 w-5 text-muted-foreground" />
          <div className="mt-3 text-sm text-muted-foreground">Başarısız deneme</div>
          <div className="num mt-1 text-2xl font-medium text-foreground">{failed}</div>
        </div>
      </section>

      {canDelete && fixtureBatches.length > 0 ? (
        <section aria-label="Test kayıtlarını temizle" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-2xl">
              <h2 className="font-medium text-foreground">Silinebilecek test kayıtları: {fixtureBatches.length} aktarım</h2>
              <p className="mt-1 text-sm text-muted-foreground">Bu işlem yalnızca sistemin test kaydı olarak işaretlediği aktarımları hedefler. Gerçek mağaza kayıtları bu seçimle hedeflenmez.</p>
            </div>
            <Button variant="destructive" onClick={() => { setBulkDeleteError(null); setConfirmBulkDelete(true); }} disabled={bulkDeleting}>
              {bulkDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Kayıtları sil
            </Button>
          </div>
          {confirmBulkDelete ? <div role="alertdialog" aria-label="Test kayıtlarını silmeyi onayla" className="mt-3 rounded-md border border-destructive/40 p-3">
            <p className="text-sm">Test olarak belirlenen {fixtureBatches.length} aktarım kalıcı olarak silinsin mi? Bu işlem geri alınamaz.</p>
            <div className="mt-3 flex gap-2"><Button variant="destructive" onClick={() => void deleteFixtureBatches()} disabled={bulkDeleting}>{bulkDeleting ? "Siliniyor…" : "Evet, kayıtları sil"}</Button><Button variant="outline" onClick={() => setConfirmBulkDelete(false)} disabled={bulkDeleting}>Hayır, vazgeç</Button></div>
          </div> : null}
          {bulkDeleteError ? <p role="alert" className="mt-3 text-sm text-destructive">{bulkDeleteError}</p> : null}
        </section>
      ) : null}
      {canDelete && bulkDeleteError && fixtureBatches.length === 0 ? <p role="alert" className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">{bulkDeleteError}</p> : null}

      <section aria-label="İçe aktarma günlüğü" className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="font-serif text-xl text-foreground">İçe aktarma günlüğü</h2>
            <p className="text-sm text-muted-foreground">Şirketin son 100 aktarım kaydı. Test kayıtları operasyonel özetlere katılmaz.</p>
          </div>
          <Button variant="outline" onClick={() => void loadBatches()} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Yenile
          </Button>
        </div>
        {error ? <div className="m-5 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">Kayıtlar yüklenemedi: {error}</div> : null}
        {loading && batches.length === 0 ? <div className="p-6 text-sm text-muted-foreground">Kayıtlar yükleniyor…</div> : null}
        {!loading && !error && batches.length === 0 ? (
          <div className="p-8 text-center">
            <History className="mx-auto h-8 w-8 text-muted-foreground" />
            <h3 className="mt-3 font-medium text-foreground">Henüz aktarım kaydı yok</h3>
            <p className="mt-1 text-sm text-muted-foreground">Yeni dosya aktarımları burada görünecek.</p>
          </div>
        ) : null}
        {pendingDelete ? (
          <div role="alertdialog" aria-label="Aktarım kaydını sil" className="m-5 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
            <div className="flex items-start gap-3">
              <Trash2 className="mt-0.5 h-5 w-5 text-destructive" />
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <h3 className="font-medium text-foreground">Aktarım kaydını ve staging satırlarını sil</h3>
                  <p className="mt-1 break-all text-sm text-muted-foreground">{displayBatchName(pendingDelete)} · {new Date(pendingDelete.created_at).toLocaleString("tr-TR")}</p>
                  <p className="mt-2 text-sm text-destructive">Bu işlem seçilen aktarımın satırlarını, doğrulama sorunlarını ve aktarım kaydını kalıcı olarak siler. Geri alınamaz. Bağlı bir tedarikçi faturası varsa veritabanı silmeyi reddeder.</p>
                </div>
                {deleteError ? <p className="text-sm text-destructive">{deleteError}</p> : null}
                <div className="flex flex-wrap gap-2">
                  <Button variant="destructive" onClick={() => void deleteBatch()} disabled={deletingId === pendingDelete.id}>
                    {deletingId === pendingDelete.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                    Evet, sil
                  </Button>
                  <Button variant="outline" onClick={cancelDelete} disabled={deletingId === pendingDelete.id}><X className="mr-2 h-4 w-4" />Hayır, vazgeç</Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {batches.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-secondary/50 text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Tarih</th><th className="px-4 py-3">Dosya</th><th className="px-4 py-3">Kaynak / Tür</th><th className="px-4 py-3">Durum</th>
                  <th className="px-4 py-3 text-right">Toplam</th><th className="px-4 py-3 text-right">Geçerli</th><th className="px-4 py-3 text-right">Hatalı</th>{canDelete ? <th className="px-4 py-3 text-right">İşlem</th> : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {batches.map((batch) => (
                  <tr key={batch.id}>
                    <td className="whitespace-nowrap px-4 py-3">{new Date(batch.created_at).toLocaleString("tr-TR")}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {displayBatchName(batch)}
                      {isTestFixtureBatch(batch) ? <span className="ml-2 rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-sans text-[10px] text-amber-800">TEST KAYDI</span> : null}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{batch.source_system ?? "kaynak yok"}{batch.import_type ? ` / ${batch.import_type}` : ""}</td>
                    <td className="px-4 py-3"><span className={`rounded-full border px-2 py-1 text-xs ${batch.status === "committed" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700" : batch.status === "failed" ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-border text-muted-foreground"}`}>{formatBatchStatus(batch.status)}</span></td>
                    <td className="px-4 py-3 text-right tabular-nums">{batch.total_rows ?? "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{batch.valid_rows ?? "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{batch.error_rows ?? "—"}</td>
                    {canDelete ? <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => { setPendingDelete(batch);; setDeleteError(null); }} disabled={!!deletingId}><Trash2 className="mr-1 h-4 w-4" />Sil</Button></td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default DataControl;
